import { useEffect, useState } from "react";

import type { PocketOption } from "../components/create-card/types";
import { DEFAULT_POCKET_COLOR, resolvePocketName } from "../constants/pockets.constants";
import { PocketsService } from "../services/pocketsService";
import type { Pocket } from "../types/pockets-api.types";

/** One page is the whole catalog in practice — an organization does not run hundreds of pockets. */
const POCKETS_LIMIT = 100;

/**
 * Wire row → picker option. Name and colour go through the module's shared helpers rather than being
 * re-derived here, so a pocket reads identically in the card modal, the pockets list and the detail page.
 */
export const toPocketOption = (pocket: Pocket): PocketOption => ({
  id: pocket.id,
  name: resolvePocketName(pocket),
  color: pocket.metadata?.color || DEFAULT_POCKET_COLOR,
  icon: pocket.metadata?.icon ?? null,
});

/**
 * The pockets offered by the new-card modal, reloaded on each opening.
 *
 * Deliberately NOT `useOrganizationPockets`: that hook pages at 10 rows, so reusing it would silently cap
 * this selector at the 10 most recent pockets — the same class of bug the holder picker already had to fix
 * (see the note in `CreateCardModal`). This reads one page of 100.
 *
 * `enabled` is the modal's `open` AND the org's pockets visibility: nothing is requested while either is
 * false.
 */
export const useCreateCardPockets = (enabled: boolean): { pockets: PocketOption[]; isLoading: boolean } => {
  const [pockets, setPockets] = useState<PocketOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      // The list is deliberately NOT cleared: the dialog stays mounted through its exit transition, so
      // emptying it here blanks the pocket out of the card preview mid-fade. The next opening overwrites it,
      // and while closed nothing reads it — `pocketId` is reset with the rest of the form.
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const { data } = await PocketsService.fetchPockets({ limit: POCKETS_LIMIT });
        if (cancelled) return;
        setPockets((Array.isArray(data?.data) ? data.data : []).map(toPocketOption));
      } catch {
        // The pocket is optional, so a failed load must not block issuance: the picker renders its own
        // "no pockets" hint and the card can still be created without one.
        if (!cancelled) setPockets([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { pockets, isLoading };
};
